import { z } from 'zod';
import {
  PhoneOutgoing,
  CalendarCheck,
  Voicemail,
  PhoneMissed,
  PhoneCall,
  Percent,
  Clock,
  Loader,
} from 'lucide-react';
import { StatTile } from '../stat-tile';
import { useDashboardMetric } from '@/hooks/use-dashboard-metric';
import type { TileDefinition } from './types';
import { pct, priorPeriodLabel } from './helpers';

/**
 * Outbound-call stat tiles. Same shape as the task stat tiles
 * (`stat-tiles.tsx`): a big number + caption + optional prior-period
 * ▲/▼. Disposition counts window on the call-concluded time; the two
 * pipeline tiles (pending / in progress) are live "right now" stocks.
 *
 * All read a single metric via `useDashboardMetric`, so each tile
 * skeletons independently.
 */

const emptyConfig = z.object({}).strict();
const statSize = { defaultSize: { w: 3, h: 2 }, minSize: { w: 2, h: 2 } } as const;

export const outboundCallsTile: TileDefinition = {
  type: 'outbound_calls',
  title: 'Outbound calls',
  description: 'Calls concluded within the selected date range.',
  icon: PhoneOutgoing,
  metrics: ['outbound_calls'],
  ...statSize,
  configSchema: emptyConfig,
  defaultConfig: {},
  render: ({ period, dashboardCtx, enabled }) => {
    const { metric, isLoading, isError } = useDashboardMetric({
      metricKey: 'outbound_calls',
      from: period.from,
      to: period.to,
      enabled,
    });
    const diff = metric && metric.priorCount != null ? metric.count - metric.priorCount : undefined;
    return (
      <StatTile
        value={metric?.count.toLocaleString()}
        caption="calls placed"
        isLoading={isLoading}
        isError={isError}
        delta={
          diff === undefined
            ? undefined
            : { diff, polarity: 'neutral', periodLabel: priorPeriodLabel(dashboardCtx.dashPreset) }
        }
      />
    );
  },
};

export const outboundScheduledTile: TileDefinition = {
  type: 'outbound_scheduled',
  title: 'Scheduled',
  description: 'Appointments booked on an outbound call.',
  icon: CalendarCheck,
  metrics: ['outbound_scheduled'],
  ...statSize,
  configSchema: emptyConfig,
  defaultConfig: {},
  render: ({ period, dashboardCtx, enabled }) => {
    const { metric, isLoading, isError } = useDashboardMetric({
      metricKey: 'outbound_scheduled',
      from: period.from,
      to: period.to,
      enabled,
    });
    const diff = metric && metric.priorCount != null ? metric.count - metric.priorCount : undefined;
    return (
      <StatTile
        value={metric?.count.toLocaleString()}
        caption="booked on the call"
        tone="positive"
        isLoading={isLoading}
        isError={isError}
        delta={
          diff === undefined
            ? undefined
            : {
                diff,
                polarity: 'up-is-good',
                periodLabel: priorPeriodLabel(dashboardCtx.dashPreset),
              }
        }
      />
    );
  },
};

export const outboundVoicemailsTile: TileDefinition = {
  type: 'outbound_voicemails',
  title: 'Voicemails left',
  description: 'Calls that reached a voicemail box.',
  icon: Voicemail,
  metrics: ['outbound_voicemails'],
  ...statSize,
  configSchema: emptyConfig,
  defaultConfig: {},
  render: ({ period, dashboardCtx, enabled }) => {
    const { metric, isLoading, isError } = useDashboardMetric({
      metricKey: 'outbound_voicemails',
      from: period.from,
      to: period.to,
      enabled,
    });
    const diff = metric && metric.priorCount != null ? metric.count - metric.priorCount : undefined;
    return (
      <StatTile
        value={metric?.count.toLocaleString()}
        caption="messages left"
        isLoading={isLoading}
        isError={isError}
        delta={
          diff === undefined
            ? undefined
            : { diff, polarity: 'neutral', periodLabel: priorPeriodLabel(dashboardCtx.dashPreset) }
        }
      />
    );
  },
};

export const outboundNotAnsweredTile: TileDefinition = {
  type: 'outbound_not_answered',
  title: 'Not answered',
  description: 'Calls that rang out without connecting.',
  icon: PhoneMissed,
  metrics: ['outbound_not_answered'],
  ...statSize,
  configSchema: emptyConfig,
  defaultConfig: {},
  render: ({ period, dashboardCtx, enabled }) => {
    const { metric, isLoading, isError } = useDashboardMetric({
      metricKey: 'outbound_not_answered',
      from: period.from,
      to: period.to,
      enabled,
    });
    const diff = metric && metric.priorCount != null ? metric.count - metric.priorCount : undefined;
    return (
      <StatTile
        value={metric?.count.toLocaleString()}
        caption="rang out"
        isLoading={isLoading}
        isError={isError}
        delta={
          diff === undefined
            ? undefined
            : {
                // Fewer unanswered calls is the good direction.
                diff,
                polarity: 'down-is-good',
                periodLabel: priorPeriodLabel(dashboardCtx.dashPreset),
              }
        }
      />
    );
  },
};

export const outboundReachedTile: TileDefinition = {
  type: 'outbound_reached',
  title: 'Reached a human',
  description: 'Calls where the patient was reached, and the connect rate.',
  icon: PhoneCall,
  metrics: ['outbound_reached'],
  ...statSize,
  configSchema: emptyConfig,
  defaultConfig: {},
  render: ({ period, dashboardCtx, enabled }) => {
    const { metric, isLoading, isError } = useDashboardMetric({
      metricKey: 'outbound_reached',
      from: period.from,
      to: period.to,
      enabled,
    });
    // Delta is the connect-rate change in percentage points.
    const diff =
      metric && metric.priorRate != null
        ? (metric.connectRate - metric.priorRate) * 100
        : undefined;
    return (
      <StatTile
        value={metric?.count.toLocaleString()}
        caption={metric ? `${pct(metric.connectRate)} connect rate` : undefined}
        tone="positive"
        isLoading={isLoading}
        isError={isError}
        delta={
          diff === undefined
            ? undefined
            : {
                diff,
                polarity: 'up-is-good',
                format: (d) => `${Math.abs(d).toFixed(1)}pp`,
                periodLabel: priorPeriodLabel(dashboardCtx.dashPreset),
              }
        }
      />
    );
  },
};

export const outboundBookingRateTile: TileDefinition = {
  type: 'outbound_booking_rate',
  title: 'Booking rate',
  description: 'Scheduled as a share of calls that reached the patient.',
  icon: Percent,
  metrics: ['outbound_booking_rate'],
  ...statSize,
  configSchema: emptyConfig,
  defaultConfig: {},
  render: ({ period, dashboardCtx, enabled }) => {
    const { metric, isLoading, isError } = useDashboardMetric({
      metricKey: 'outbound_booking_rate',
      from: period.from,
      to: period.to,
      enabled,
    });
    const diff =
      metric && metric.priorRate != null ? (metric.rate - metric.priorRate) * 100 : undefined;
    return (
      <StatTile
        value={metric ? pct(metric.rate) : undefined}
        caption={
          metric
            ? `${metric.scheduled.toLocaleString()} of ${metric.reached.toLocaleString()} reached`
            : undefined
        }
        tone="positive"
        isLoading={isLoading}
        isError={isError}
        delta={
          diff === undefined
            ? undefined
            : {
                diff,
                polarity: 'up-is-good',
                format: (d) => `${Math.abs(d).toFixed(1)}pp`,
                periodLabel: priorPeriodLabel(dashboardCtx.dashPreset),
              }
        }
      />
    );
  },
};

export const outboundPendingTile: TileDefinition = {
  type: 'outbound_pending',
  title: 'Pending calls',
  description: 'Outbound calls queued to dial.',
  icon: Clock,
  metrics: ['outbound_pending'],
  ...statSize,
  configSchema: emptyConfig,
  defaultConfig: {},
  render: ({ enabled }) => {
    const { metric, isLoading, isError } = useDashboardMetric({
      metricKey: 'outbound_pending',
      enabled,
    });
    return (
      <StatTile
        value={metric?.count.toLocaleString()}
        caption="queued to dial"
        isLoading={isLoading}
        isError={isError}
      />
    );
  },
};

export const outboundInProgressTile: TileDefinition = {
  type: 'outbound_in_progress',
  title: 'In progress',
  description: 'Outbound calls live right now.',
  icon: Loader,
  metrics: ['outbound_in_progress'],
  ...statSize,
  configSchema: emptyConfig,
  defaultConfig: {},
  render: ({ enabled }) => {
    const { metric, isLoading, isError } = useDashboardMetric({
      metricKey: 'outbound_in_progress',
      enabled,
    });
    return (
      <StatTile
        value={metric?.count.toLocaleString()}
        caption="on a call now"
        isLoading={isLoading}
        isError={isError}
      />
    );
  },
};
